/**
 * @Description 相机控制器类，处理部分键鼠交互以及改变摄像机姿态方法
 * @Author iCEA
 * @Date 2020-10-29
 * @Version 1.0
 * @todo 1、2D正交相机的一系列交互
 */

import TWEEN from 'tween.js'
import * as THREE from 'three'
import * as Utils from './Utils'
import { Material } from './ModelMaterial_offscreen'
import { HotpointSet } from './Hotpoint'
import { Scene } from './Scene'
import { CPointSet } from './CameraPoints'
import { Camera } from './Camera'
import { Renderer } from './Renderer'
import { Compass } from './Compass'
import { GodMode } from './GodMode'
import { PanoMode } from './PanoMode'
import { Listener } from './Listener'
import { InputBase } from './Input/InputBase'
import { config } from './config'
import { Measure } from './Measure'
import { EventEmitter } from 'events/events'
import { Events } from './Events'

const ControllerState = {
  Preparing: 0,
  Wheeling: 1,
  Idle: 2,
  Damping: 3
}

class CameraController {
  /**
   * 构造函数
   * @param {Camera} cam 需要被控制的相机
   * @param {CPointSet} cps 点位集合
   * @param {Material} mat 材质
   * @param {Scene} scene 场景
   * @param {Renderer} renderer 渲染器
   * @param {SingleView} singleView 单场景
   * @param {Boolean} isOnlyPono 保存模型视角状态，true为2D，false为3D
   */
  constructor (cam, cps, mat, scene, renderer, singleView, isOnlyPono = true) {
    /**
     * @type {Camera} 相机单例
     */
    this.camera = cam
    /**
     * @type {number} 默认跳转动画时长
     */
    this.transitionTime = config.PanoMode.TransitionTime
    /**
     * @type {boolean} 函数锁，用于tween动画控制
     */
    this.functionLock = false
    /**
     * @type {boolean} 中断标志，中断场景漫游包括自动播放
     */
    this.interrupt = false
    /**
     * @type {boolean} 是否可旋转
     */
    this.rotatable = true
    /**
     * @type {number} 前端切换模型/全景视角时暂存的点位
     */
    this.savePoint = this.camera.cameraCurrentIndex

    /**
     * @type {boolean} 保存模型视角状态，true为2D，false为3D
     */
    this.onlyPono = isOnlyPono

    // 伪阻尼动画相关
    /**
     * @type {object} 阻尼动画
     */
    this.damping = null
    /**
     * @type {number} 阻尼动画
     */
    this.rotateState = ControllerState.Idle
    /**
     * @type {number} 当前帧与上一帧经度差
     */
    this.delta_lon = 0
    /**
     * @type {number} 当前帧与上一帧纬度差
     */
    this.delta_lat = 0
    /**
     * @type {number} 前一帧与前前一帧的经度差
     */
    this.p_delta_lon = 0
    /**
     * @type {number} 前一帧与前前一帧的纬度差
     */
    this.p_delta_lat = 0

    // 资源释放相关
    /**
     * @type {number} 当前渲染到的帧数，requestAnimationFrame返回的值
     */
    this.id = 0
    /**
     * @type {boolean} 是否继续获取下一帧，在释放资源方法内设置为false停止其自动获取下一帧
     */
    this.runtimeFlag = true

    /**
     * @type {EventEmitter} 事件触发器
     */
    this.events = new EventEmitter()

    /**
     * @type {Listener} 显示3/2D切换按钮
     */
    this.showSwitchListener = new Listener()
    /**
     * @type {Listener} 隐藏3/2D切换按钮
     */
    this.hideSwitchListener = new Listener()

    /**
     * @type {CPointSet} 点位集合
     */
    this.cps = cps
    /**
     * @type {Material} 材质
     */
    this.mat = mat
    /**
     * @type {Renderer} 渲染器
     */
    this.renderer = renderer
    /**
     * @type {Scene} 场景
     */
    this.scene = scene

    /**
     * @type {SingleView} 单场景
     */
    this.singleView = singleView
    /**
     * @type {GodMode} 上帝视角控制器
     */
    this.godModeCtrl = new GodMode(this.camera, this.singleView, this.renderer)
    /**
     * @type {PanoMode} 全景视角控制器
     */
    this.panoModeCtrl = new PanoMode(this.camera, this.cps, this.transitionTime, this.renderer, this.singleView)

    /**
     * @type {InputBase[]} inputs 输入组件数组
     */
    this.inputs = []
  }

  setSceneCenter (center) {
    this.godModeCtrl.center = center
  }

  /**
   * 添加输入组件
   * @param {InputBase} input 输入组件
   */
  addInput (input) {
    const findIdx = this.inputs.findIndex(i => i === input)
    if (findIdx === -1) {
      this.inputs.push(input)
      input.cameraController = this
    }
  }

  /**
   * 移除输入组件
   * @param {InputBase} input 输入组件
   */
  removeInput (input) {
    const findIdx = this.inputs.findIndex(i => i === input)
    if (findIdx !== -1) {
      this.inputs.splice(findIdx, 1)
      input.cameraController = null
    }
  }

  /**
   * 初始化，禁用右键菜单
   */
  initCameraController () {
    document.oncontextmenu = (event) => {
      event.preventDefault()
    }

    this.compatible()
  }

  /**
   * 兼容其他浏览器的requestAnimationFrame
   */
  compatible () {
    window.requestAnimationFrame = window.requestAnimationFrame ||
    window.webkitRequestAnimationFrame ||
    window.mozRequestAnimationFrame ||
    window.msRequestAnimationFrame ||
    window.oRequestAnimationFrame ||
    function (callback) {
      // 为了使setTimteout的尽可能的接近每秒60帧的效果
      window.setTimeout(callback, 1000 / 60)
    }

    window.cancelAnimationFrame = window.cancelAnimationFrame ||
    window.webkitCancelAnimationFrame ||
    window.mozCancelAnimationFrame ||
    window.msCancelAnimationFrame ||
    window.oCancelAnimationFrame ||
    function (id) {
      // 为了使setTimteout的尽可能的接近每秒60帧的效果
      window.clearTimeout(id)
    }
  }

  /**
   * [tween] 阻尼效果动画，鼠标根据前后帧的经纬度相减实现，键盘根据前后帧经纬度的变化程度相减实现。
   */
  dampingEffect () {
    const cam = this.camera
    const t = this

    const lonlat = {
      u: this.p_delta_lon * config.Damping.distance,
      v: this.p_delta_lat * config.Damping.distance
    }
    this.damping = new TWEEN.Tween(lonlat)
    this.damping.to({
      u: 0,
      v: 0
    }, config.Damping.time)
    this.damping.onUpdate(() => {
      cam.lon += lonlat.u
      cam.lat += lonlat.v
    })
    this.rotateState = ControllerState.Damping
    this.damping.easing(config.Damping.easingFunction)
    this.damping.onComplete(() => {
      t.rotateState = this.rotateState === ControllerState.Damping ? ControllerState.Idle : t.rotateState
    })
    this.damping.start()
  }

  /**
   * 中断拖拽动画
   */
  breakDamping () {
    if (this.damping) {
      TWEEN.remove(this.damping)
      this.damping = null
      this.rotateState = ControllerState.Idle
    }
  }

  /**
   * 打断动画、目前打断的是toView方法、拖拽动画
   */
  async breakAnimate (isInteract) {
    this.breakDamping()
    this.interrupt = true
  }

  /**
   * 可中断动画后恢复
   */
  beforeBreakableAnimate () {
    this.functionLock = true
  }

  /**
   * 可中断动画后恢复
   */
  afterBreakableAnimate () {
    this.functionLock = false
  }

  /**
   * 强限制性动画前开启，防止输入中断
   * @param {boolean} lockInput 锁输入
   */
  beforeStrongAnimate (lockInput = true) {
    for (const i of this.inputs) {
      i.enable = !lockInput
    }
    this.functionLock = true
    this.rotatable = false
  }

  /**
   * 强限制性动画后恢复，恢复状态
   */
  afterStrongAnimate () {
    for (const i of this.inputs) {
      i.enable = true
    }
    this.functionLock = false
    this.rotatable = true
  }

  /**
   * 在前往上帝视角前，发出事件
   */
  beforeToGod () {
    // todo切换模型材质
    this.events.emit(Events.BeforeToGod)
  }

  /**
   * 成功回到全景视角需要调用
   * @param {number} targetIdx 目标位置
   * @param {THREE.Vector3} targetPos 目标位置对应的坐标
   */
  afterToPano (targetIdx, targetPos) {
    this.camera.cameraCurrentIndex = targetIdx
    // 重新计算热点
    this.events.emit(Events.ToPano, targetIdx, targetPos)
  }

  /**
   * [tween] 模型视角下3D透视相机转2D正交相机
   */
  async to2D () {
    if (this.functionLock) return
    this.beforeStrongAnimate()
    if (this.camera.cameraMode) {
      this.beforeToGod()
    }

    if (this.singleView.nowSingle) {
      // 如果当前在单场景2，则需要淡入淡出回来主场景的上帝2d视角
      await this.godModeCtrl.fadeTo2D(() => {
        this.events.emit(Events.To2D)
      })
    } else {
      // 正常回退到上帝2D视角
      this.savePoint = this.camera.cameraCurrentIndex
      await this.godModeCtrl.normal2Orth()
      this.events.emit(Events.To2D)
    }

    this.afterStrongAnimate()
  }

  /**
   * 从正交相机恢复成透视相机
   * @param {number} tlon 回调函数参数
   * @param {number} tlat 回调函数参数
   * @param {boolean} toView 是否下降到全景视角的标志
   * @param {number} singleIdx 单场景ID，仅进入单场景点才用到第四个参数
   */
  async to3D (tlon = 0, tlat = 0, toView = false, singleIdx = 0) { // 仅进入单场景点才用到第四个参数
    if (this.functionLock || this.camera.cameraType) return
    this.beforeStrongAnimate()

    if (singleIdx !== 0 && Utils.isSingleView(singleIdx)) {
      // 单场景点位淡入淡出
      try {
        await this.mat.prepareForTranslate(singleIdx, config.SingleView.Matrix)
      } catch (e) {
        this.afterStrongAnimate()
        throw e
      }
      await this.godModeCtrl.fade2dToPano(singleIdx, tlon, tlat, () => {
        this.events.emit(Events.ToPano, singleIdx, 0)
      })
      this.afterStrongAnimate()
      // function end
    } else {
      // 非单场景点情况下可以使用cps.getCameraPoint
      const targetIdx = this.camera.cameraDestinationIndex
      const targetPos = this.cps.getCameraPoint(targetIdx).position.clone()
      if (this.camera.cameraCurrentIndex !== targetIdx) {
        try {
          this.mat.prepareForTranslate(targetIdx, this.cps.getCameraPoint(targetIdx).correctMat)
        } catch (e) {
          this.afterStrongAnimate()
          throw e
        }
      }
      await this.godModeCtrl.orth2Normal(targetPos, toView)
      this.afterStrongAnimate()
      if (toView) {
        this.afterToPano(targetIdx, targetPos)
      } else {
        this.events.emit(Events.To3D)
      }
      // function end
    }
  }

  /**
   * 上帝2D视角切换到全景视角
   * @param {number} tlon 回调函数参数
   * @param {number} tlat 回调函数参数
   * @param {number} singleIdx 单场景ID，仅进入单场景点才用到
   */
  async orth2View (tlon = 0, tlat = 0, singleIdx = 0) { // 仅进入单场景点才用到第三个参数
    await this.to3D(tlon, tlat, true, singleIdx) // 仅进入单场景点才用到第四个参数
  }

  /**
   * [tween] 上帝3D视角切换到全景视角
   * @param {number} tlon 目标经度
   * @param {number} tlat 目标纬度
   * @param {number} singleIdx 单场景ID，仅进入单场景点才用到
   */
  async change2View (tlon = 0, tlat = 0, singleIdx = 0) { // 仅进入单场景点才用到最后一个参数
    if (this.functionLock) return

    const cam = this.camera
    this.beforeStrongAnimate()
    if (singleIdx !== 0 && Utils.isSingleView(singleIdx)) {
      // 单场景点位淡入淡出
      try {
        await this.mat.prepareForTranslate(singleIdx, config.SingleView.Matrix)
      } catch (e) {
        this.afterStrongAnimate()
        throw e
      }
      await this.godModeCtrl.fade3dToPano(singleIdx, tlon, tlat, () => {
        this.events.emit(Events.ToPano, singleIdx, 0)
      })
      this.afterStrongAnimate()
      // end function
    } else {
      // 常规点位动画切换
      const targetIdx = cam.cameraDestinationIndex
      const targetPos = this.cps.getCameraPoint(targetIdx).position.clone()
      if (cam.cameraCurrentIndex !== targetIdx) {
        try {
          await this.mat.prepareForTranslate(targetIdx, this.cps.getCameraPoint(targetIdx).correctMat)
        } catch (e) {
          this.afterStrongAnimate()
          throw e
        }
      }

      cam.cameraMode = true
      cam.g2vsign = true
      cam.lon = cam.lon + 180
      cam.lat = -cam.lat
      cam.viewUpdate()

      // 变换动画
      await this.godModeCtrl.normal2Pano(targetPos, tlon, tlat)
      cam.g2vsign = false
      this.afterStrongAnimate()
      this.afterToPano(targetIdx, targetPos)
      // end function
    }
  }

  /**
   * [tween] 全景切换到上帝3D视角
   */
  async change2God () {
    if (this.functionLock) return
    this.beforeStrongAnimate()

    // 隐藏热点
    this.beforeToGod()

    if (this.singleView.nowSingle) {
      // 如果当前在单场景2，则需要淡入淡出回来主场景的模型3d视角
      await this.godModeCtrl.fadeTo3D(() => {
        this.events.emit(Events.To3D) // 需要在黑屏中完成指南针切换
      }, this.cps.getCameraPoint(this.savePoint).position)
    } else {
      // 如果不在单场景则正常动画切换
      this.savePoint = this.camera.cameraCurrentIndex
      await this.godModeCtrl.pano2Normal()
      this.events.emit(Events.To3D) // 动画后完成指南针切换
    }

    this.afterStrongAnimate()
    this.camera.v2gsign = false
  }

  /**
   * [tween] 点位直接跳转位置
   * @param {THREE.Vector3} cameraPOS 相机位置坐标
   * @param {number} idx 相机目标点位下标
   */
  async animateCamera (cameraPOS, idx) {
    if (this.functionLock) return
    this.functionLock = true
    try {
      await this.mat.prepareForTranslate(idx, this.cps.getCameraPoint(idx).correctMat)
    } catch (e) {
      this.functionLock = false
      throw e
    }

    this.events.emit(Events.BeforeWalkTo, idx)
    this.mat.updateTexture(this.transitionTime)
    await this.panoModeCtrl.translateTo(idx, this.camera.lon, this.camera.lat)
    this.events.emit(Events.AfterWalkTo, idx)
    this.events.emit(Events.VisitPano, idx)

    this.functionLock = false
  }

  /**
   * [tween] 下方场景点位间漫游
   * @param {number} sV 起始点位下标
   * @param {number} tV 目标点位下标
   * @param {number} tlon 目标点位经度
   * @param {number} tlat 目标点位纬度
   * @param {boolean} fastTravel 快速导航（到目标点后是否转视角，开则不转）
   * @param {number} goTime 动画时间，用以自动播放速度控制
   */
  async toView (sV, tV, tlon, tlat, fastTravel, goTime = this.transitionTime, in3d = false) {
    if (this.functionLock) return
    // 键盘+函数锁
    this.beforeStrongAnimate(false)

    if (sV !== tV) {
      const route = this.cps.findPath(sV, tV)
      const cameraLons = []
      // 构造cameraLons
      cameraLons.push(this.camera.lon) // 第0个
      for (let i = 1; i < route.length - 1; ++i) {
        const dir = new THREE.Vector3()
        dir.subVectors(this.cps.getCameraPoint(route[i + 1]).position, this.cps.getCameraPoint(route[i]).position)
        const llon = THREE.MathUtils.radToDeg(Math.atan2(dir.z, dir.x))
        cameraLons.push(llon)
      }
      cameraLons.push(cameraLons[cameraLons.length - 1])
      // todo 调整快速导航的做法
      // console.log('旋转视角')
      // console.log(cameraLons) // cameraLons:从1开始存放着下一位置的相机视角，0为当前相机视角,最后一个视角与倒数第二个视角相同

      try {
        if (in3d) {
          await this.walkingCameraIn3d(route, cameraLons, tlon, tlat, goTime / this.transitionTime)
        } else {
          await this.walkingCamera(route, cameraLons, tlon, tlat, goTime / this.transitionTime)
        }
      } catch (e) {
        this.interrupt = true
        // todo：处理因为贴图加载失败而打断的情况
        this.afterStrongAnimate()
        throw e
      }
    }

    if (sV === tV || !fastTravel) {
      if (this.interrupt) { // 停止播放
        this.afterStrongAnimate()
        return Promise.reject(new Error('stop in interrupt'))
      }
      await this.panoModeCtrl.lookTo(tlon, tlat, goTime / this.transitionTime * 1.5)
    }
    this.events.emit(Events.VisitPano, tV)
    this.afterStrongAnimate()
  }

  /**
   * [tween] 原地旋转至目标经纬度，在快速导航关闭的情况下配合点位漫游动画使用
   * @param {number} tlon 目标经度
   * @param {number} tlat 目标纬度
   * @param {number} goTime 执行动画时间，自动播放控制速度
   */
  async turn2View (tlon, tlat, goTime) {
    // 此函数为原地不动，仅旋转视角到目标点位，同时解锁函数锁、鼠标锁、键盘锁
    if (this.functionLock) return
    this.beforeBreakableAnimate()

    await this.panoModeCtrl.lookTo(tlon, tlat)
    this.afterBreakableAnimate()
  }

  async walkingCamera (routes, lons, tlon, tlat, speedRatio) {
    // 此函数为点位间一个个跳转的同时旋转视角，实现游览，贴图变化可以在此添加修改

    for (let routeIdx = 1; routeIdx < routes.length; routeIdx++) {
      if (this.interrupt) { // 停止播放
        return Promise.reject(new Error('stop in interrupt'))
      }

      const targetIdx = routes[routeIdx]

      await this.mat.prepareForTranslate(targetIdx, this.cps.getCameraPoint(targetIdx).correctMat)

      this.events.emit(Events.BeforeWalkTo, targetIdx)
      this.mat.updateTexture(this.transitionTime * speedRatio)
      // console.log(`from ${this.camera.cameraCurrentIndex}, ${this.camera.lon} to ${targetIdx}, ${lons[routeIdx]}`)
      await this.panoModeCtrl.translateAndRotateTo(targetIdx, lons[routeIdx], 0, speedRatio)

      this.events.emit(Events.AfterWalkTo, targetIdx)
    }
  }

  /**
   * [tween] 点位漫游动画
   * @param {number[]} routes 漫游点位index列表
   * @param {number[]} lons 漫游点位lon列表
   * @param {number} tlon 到达终点后的目标经度（若开启快速导航则无效）
   * @param {number} tlat 到达终点后的目标纬度（若开启快速导航则无效）
   * @param {number} speedRatio 执行动画倍率，自动播放控制速度
   */
  async walkingCameraIn3d (routes, lons, tlon, tlat, speedRatio) {
    this.events.emit(Events.BeforeWalkIn3D)

    for (let routeIdx = 1; routeIdx < routes.length; routeIdx++) {
      if (this.interrupt) { // 停止播放
        this.events.emit(Events.AfterWalkIn3D)
        return Promise.reject(new Error('stop in interrupt'))
      }

      const targetIdx = routes[routeIdx]

      await this.mat.prepareForTranslate(targetIdx, this.cps.getCameraPoint(targetIdx).correctMat)

      this.events.emit(Events.BeforeWalkTo, targetIdx)
      this.mat.updateTexture(this.transitionTime * speedRatio)
      // console.log(`from ${this.camera.cameraCurrentIndex}, ${this.camera.lon} to ${targetIdx}, ${lons[routeIdx]}`)
      await this.panoModeCtrl.translateAndRotateTo(targetIdx, lons[routeIdx], 0, speedRatio)

      this.events.emit(Events.AfterWalkTo, targetIdx)
    }
    this.events.emit(Events.AfterWalkIn3D)
  }

  /**
   * 走然后再看向目标
   * @param {number} targetId 目标点idx
   * @param {number} lon 目标点经度
   * @param {number} lat 目标点纬度
   */
  async walkThenLookAt (targetId, lon, lat) {
    this.beforeStrongAnimate()
    if (this.camera.cameraCurrentIndex !== targetId) {
      // todo 错误处理
      await this.mat.prepareForTranslate(targetId, this.cps.getCameraPoint(targetId).correctMat)
      this.events.emit(Events.BeforeWalkTo, targetId)
      this.mat.updateTexture(this.transitionTime)
      await this.panoModeCtrl.translateTo(targetId, this.camera.lon, this.camera.lat)
      this.events.emit(Events.AfterWalkTo, targetId)
    }
    await this.panoModeCtrl.lookTo(lon, lat)
    this.afterStrongAnimate()
  }

  /**
   * 点位淡入淡出的切换效果
   * @param {number} idx 目标点位下标
   * @param {number} tlon 目标点位经度
   * @param {number} tlat 目标点位纬度
   */
  async fadeIn (idx, tlon, tlat) {
    if (this.functionLock) return
    this.beforeStrongAnimate()

    try {
      if (Utils.isSingleView(idx)) {
        // 如果idx大于1000则说明是孤立点，额外单独处理。
        await this.mat.prepareForTranslate(idx, config.SingleView.Matrix)
        this.events.emit(Events.BeforeWalkTo, idx)
        await this.panoModeCtrl.pano2Single(idx, tlon, tlat, () => {
          this.mat.newTextureDone()
        })
      } else {
        // console.log('idx小于1000 目标为常规点位')
        await this.mat.prepareForTranslate(idx, this.cps.getCameraPoint(idx).correctMat)
        this.events.emit(Events.BeforeWalkTo, idx)
        await this.panoModeCtrl.fadeTo(idx, tlon, tlat, () => {
          this.mat.newTextureDone()
        })
      }
      // todo 将fade到目标作为事件暴露出来
      this.events.emit(Events.AfterWalkTo, idx) // idx大于1K怎么办？
    } catch (e) {
      this.afterStrongAnimate()
      throw e
    }

    this.afterStrongAnimate()
  }

  /**
   * 每帧调用，控制更新函数。处理键鼠事件，加上damping效果，调用相机的更新函数。
   */
  update () {
    if (this.tranFlag) {
      this.breakAnimate()
    }

    this.transformCamera()
    if (this.camera.cameraMode) this.camera.viewUpdate()
    else if (!this.camera.v2gsign) this.camera.godUpdate()
  }

  /**
   * 变换摄像机，每帧调用
   */
  transformCamera () {
    const cam = this.camera
    // if (this.dRotate.lengthSq() !== 0 || this.dTranslate.lengthSq() !== 0 || this.dZoom !== 0 || this.tranFlag) {
    // todo 删除
    // console.log(this.dRotate, this.dTranslate, this.dZoom, this.tranFlag)
    // }

    // 旋转
    if (this.rotatable) {
      this.p_delta_lon = this.delta_lon
      this.p_delta_lat = this.delta_lat
      this.delta_lon = this.dRotate.x // 刷新△
      this.delta_lat = this.dRotate.y
      // 如果△为0表示不动了，不为0表示动了
      // 旋转
      cam.lon += this.dRotate.x
      cam.lat += this.dRotate.y
      // 状态变换
      if (this.rotateState === ControllerState.Wheeling) {
        if (!this.tranFlag && (this.p_delta_lat !== 0 || this.p_delta_lon !== 0)) {
          // 触发damping
          this.rotateState = ControllerState.Damping
          this.dampingEffect()
        }
      } else if (this.tranFlag) {
        this.rotateState = ControllerState.Wheeling
      }
    }

    if (!this.functionLock) {
      // 平移
      if (this.dTranslate.x !== 0 || this.dTranslate.y !== 0) {
        cam.panning(this.dTranslate.x, this.dTranslate.y)
      }

      // 缩放
      if (this.dZoom !== 0) {
        if (cam.cameraMode) {
        // 相机的视觉随着鼠标滚动的距离拉进或者远离
          cam.fov += this.dZoom
          cam.fov = THREE.MathUtils.clamp(cam.fov, 45, 65)
          cam.camera.updateProjectionMatrix()
        } else {
          if (cam.cameraType) {
            cam.zoom += this.dZoom
          } else {
            cam.camera.zoom += this.dZoom
            cam.camera.zoom = cam.camera.zoom > 0.5 ? (cam.camera.zoom < 2.0 ? cam.camera.zoom : 2.0) : 0.5
            cam.camera.updateProjectionMatrix()
          }
        }
      }
    }
  }

  /**
   * 主渲染循环，默认1秒调用60次。
   */
  animate () {
    // 如果还没停下来，则继续执行动画，获取id
    if (this && this.runtimeFlag) this.id = window.requestAnimationFrame(this.animate.bind(this))

    // 绑定事件更新

    this.dRotate = new THREE.Vector2(0, 0)
    this.dTranslate = new THREE.Vector2(0, 0)
    this.tranFlag = false
    this.dZoom = 0

    // 更新相机坐标
    for (const input of this.inputs) {
      input.camUpdate()
      this.dRotate.add(new THREE.Vector2(input.dlon, input.dlat))
      this.dTranslate.add(input.dtranslate)
      this.dZoom += input.dzoom
      this.tranFlag = this.tranFlag || input.activate
      input.update()
    }

    TWEEN.update()
    this.update()

    for (const input of this.inputs) {
      input.clearTransformation()
    }

    this.events.emit(Events.Update)
    this.renderer.renderer.render(this.scene.scene, this.camera.camera)
  }

  /**
   * 移动到上帝视角
   */
  async toGod () {
    if (this.functionLock || this.onlyPono) return
    this.showSwitchListener.invoke() // 切换上去就显示

    if (this.camera.cameraType) {
      await this.change2God()
    } else {
      await this.to2D()
    }
  }

  /**
   * 移动到全景视角
   * @param {number|undefined} targetIdx 目标idx
   */
  async toPano (targetIdx = this.savePoint, lon = 0, lat = 0) { // lon lat 仅进入单场景点用到
    if (this.functionLock) return
    if (!Utils.isSingleView(targetIdx)) {
      this.camera.cameraDestinationIndex = targetIdx
    }
    this.hideSwitchListener.invoke() // 切换下来就隐藏
    if (this.camera.cameraType) {
      // 3D下到全景
      await this.change2View(lon, lat, targetIdx) // 仅进入单场景点才用到这三个参数
    } else {
      // 2D下到全景视角
      await this.orth2View(lon, lat, targetIdx) // TODO仅进入单场景点才用到这三个参数
    }
  }

  /**
   * 变换到2d视角
   */
  async transfer2D () {
    if (this.camera.cameraMode) {
      this.breakAnimate()
      await this.to2D()
    } else {
      if (this.camera.cameraType) {
        this.breakAnimate()
        await this.to2D()
      }
    }
  }

  /**
   * 变换到3d视角
   */
  async transfer3D () {
    if (this.camera.cameraMode) {
      this.breakAnimate()
      this.camera.cameraType = true
      await this.change2God()
    } else {
      if (!this.camera.cameraType) {
        this.breakAnimate()
        await this.to3D()
      }
    }
  }
}

export {
  CameraController
}
